import { HttpClient, HttpResponse } from '@angular/common/http';
import { environment } from '@environments/environment';
import { catchError, Observable, throwError, timeout } from 'rxjs';
import { map } from 'rxjs/operators';
import { Paginated } from '@app/core/models/pagination';
import { inject } from '@angular/core';
import { stringify } from 'qs';
import { MaintenanceService } from '@app/core/maintenance/maintenance.service';
import { MaintenanceErrorHandler } from '@app/core/maintenance/maintenance-error-handler';

const getEncoder =
  () =>
  (
    str: string,
    defaultEncoder: (
      str: any,
      defaultEncoder?: any,
      charset?: string,
    ) => string,
    charset: string,
    type: 'key' | 'value',
  ) => {
    if (type === 'key' && str.includes('$search')) {
      return '$search';
    }
    return defaultEncoder(str);
  };

type HttpMethod = 'GET' | 'PATCH' | 'DELETE' | 'POST';
const REQUEST_TIMEOUT = 10000;

export abstract class CrudService<T = any> {
  protected baseUrl?: string;
  protected authToken?: string;
  protected http = inject(HttpClient);
  protected maintenanceService = inject(MaintenanceService);
  protected errorHandler = new MaintenanceErrorHandler();

  protected constructor(protected endpoint: string) {
    this.baseUrl = environment.baseUrl;
    this.authToken = environment.authToken;
  }

  public get<U = T>(endpoint?: string) {
    return (id?: string): Observable<U> => {
      return this.request<U>('GET', this.getUrl(id, endpoint), {
        withCredentials: true,
        headers: this.getHeaders(),
        observe: 'response',
        responseType: 'json',
      }).pipe(map((res) => res.body as U));
    };
  }

  public list<U = T>(endpoint: string) {
    return (params?: any): Observable<Paginated<U>> => {
      const paramsString = stringify(params, {
        encoder: getEncoder(),
      });
      return this.request<Paginated<U>>(
        'GET',
        this.getUrl('', endpoint + `?${paramsString}`),
        {
          withCredentials: true,
          headers: this.getHeaders(),
          observe: 'response',
          responseType: 'json',
        },
      ).pipe(map((res) => res.body as Paginated<U>));
    };
  }

  public create<U = T>(
    endpoint: string,
    options?: any,
    // options?: HttpOptions,
    contentType?: string,
  ) {
    return (body?: Partial<U>, params?: any): Observable<U> => {
      return this.request<U>('POST', this.getUrl('', endpoint), {
        body,
        withCredentials: true,
        headers: this.getHeaders(contentType),
        observe: 'response',
        responseType: 'json',
        params,
        ...options,
      }).pipe(map((res) => (res as any).body as any));
    };
  }

  public update(endpoint?: string) {
    return (
      id: string,
      body: Partial<T>,
      params?: any,
    ): Observable<T | null> => {
      return this.request<T>('PATCH', this.getUrl(id, endpoint), {
        body,
        withCredentials: true,
        headers: this.getHeaders(),
        observe: 'response',
        responseType: 'json',
        params,
      }).pipe(map((res) => (res?.body as T) ?? null));
    };
  }

  public delete(endpoint: string) {
    return (id: string, params?: any): Observable<T> => {
      return this.request<T>('DELETE', this.getUrl(id, endpoint), {
        withCredentials: true,
        headers: this.getHeaders(),
        observe: 'response',
        responseType: 'json',
        params,
      }).pipe(map((res) => res.body as T));
    };
  }

  protected getUrl(param?: string, endpoint?: string) {
    let url = `${this.baseUrl}`;
    if (this.endpoint) {
      url += `/${this.endpoint}`;
    }

    if (endpoint) {
      url += `/${endpoint}`;
    }
    if (param) {
      url += `/${param}`;
    }
    return url;
  }

  protected getHeaders(contentType?: string) {
    return {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Authorization: `Basic ${this.authToken}`,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Content-Type': contentType ?? 'application/json',
    };
  }

  private request<U>(method: HttpMethod, url: string, options: any) {
    return this.http
      .request<HttpResponse<U>>(method, url, {
        ...options,
        observe: 'response',
      })
      .pipe(
        map((res) => res as HttpResponse<U>),
        timeout({
          each: REQUEST_TIMEOUT,
          with: (err) => {
            this.maintenanceService.navigateServiceUnavailable();
            return throwError(() => new Error(`request timeout ${err}`));
          },
        }),
        catchError((error) => {
          this.errorHandler.handleError(error);
          return throwError(() => error);
        }),
      );
  }
}
